import React, {useEffect, useMemo, useRef, useState, useCallback} from "react";
import {loadOmeZarr, MultiscaleImageLayer, VivViewer, DetailView,
        getChannelStats} from "@hms-dbmi/viv";
import {PolygonLayer} from "@deck.gl/layers";
import "./App.css";
import {api, getKey, parsePicks, loadSample, requestExport, dataUrl,
        loadWindows, saveWindow, manifestUrl, pxUmForSid, type SampleData, type Pick,
        type MarkerWindow} from "./api";
import {hexToRgb, rectInWm, wmClass, type Ring} from "./wm";
import {canonicalColor, isVessel} from "./palette";
import {linearRenderSubLayers} from "./linearInterp";
import {ChannelPanel, type ChannelState} from "./ChannelPanel";
import {Registration, getReviewer, clearReviewer, fetchStatus} from "./Registration";
import {Minimap} from "./Minimap";
import {WmOverlay} from "./WmOverlay";
import {ScaleBar} from "./ScaleBar";
import {CalibrationGate} from "./CalibrationGate";

const VIEW_ID = "detail";

export function App() {
  // --- URL params ---------------------------------------------------------
  const params = new URLSearchParams(location.search);
  // sid is STATE (not a constant) so we can swap samples in place — no full
  // page reload, no cold Viv boot — while keeping the reviewer's channel setup.
  const [sid, setSid] = useState<string | null>(params.get("sid"));
  const targetMarker = params.get("marker");
  // Locked-rect picker — when both lock_w + lock_h are set, drag-to-pick
  // becomes click-to-pick at a fixed L0-pixel size matching the CAD
  // representative for this marker. Lets CTL picks be acquired at the
  // exact dimensions of the CAD picks for honest magnification matching.
  const lockW = parseInt(params.get("lock_w") || "0", 10);
  const lockH = parseInt(params.get("lock_h") || "0", 10);
  const isLocked = lockW > 0 && lockH > 0;

  // --- state --------------------------------------------------------------
  const [sample, setSample] = useState<SampleData | null>(null);
  const [loader, setLoader] = useState<any>(null);
  const [error, setError] = useState<string>("");
  const [statusMsg, setStatusMsg] = useState<string>("Loading…");
  const [channels, setChannels] = useState<ChannelState[]>([]);
  // Lab-canonical per-marker windows (shared source, also edited on Exports).
  // Applied on load so every picking session starts on canonical; tweaks are
  // "exploratory" and gated at save time (see commitPick).
  const [windows, setWindows] = useState<Record<string, MarkerWindow>>({});
  // Guard so the calibration gate only renders AFTER the canonical windows have
  // actually been fetched — otherwise the empty initial {} would flash the gate
  // for every marker on first paint.
  const [windowsLoaded, setWindowsLoaded] = useState<boolean>(false);
  // Save-gate modal: set when a pick is committed with off-canonical channels.
  // stage "choose" = update-vs-revert prompt; "reverted" = canonical preview.
  const [pendingPick, setPendingPick] = useState<{
    r: {x:number;y:number;w:number;h:number};
    overlap: number;
    adjusted: Array<{label: string; cur: {lo:number;hi:number;color:string};
                     canon: MarkerWindow}>;
    stage: "choose" | "reverted";
  } | null>(null);
  const [picks, setPicks] = useState<Pick[]>([]);
  // Default to "pick" mode for locked-rect URLs — there's no reason to
  // pan around just to click once and commit. Saves a click + makes the
  // cyan HUD visible from the first frame.
  const [mode, setMode] = useState<"pan" | "pick">(isLocked ? "pick" : "pan");
  const [dragRect, setDragRect] = useState<{x:number;y:number;w:number;h:number} | null>(null);
  const [viewState, setViewState] = useState<any>(null);
  // Selections are ALWAYS perfect squares so exported regions never need
  // post-crop. presetUm > 0 turns the tool into a fixed-size square placer (a
  // click drops a square of that physical size, standardizing region size
  // across samples/diseases); presetUm === 0 is a free square drag.
  const [presetUm, setPresetUm] = useState<number>(0);
  const PRESET_UMS = [50, 100, 200, 500, 1000, 2000, 5000];

  const dragStart = useRef<[number, number] | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<any>(null);
  // Deep-link: ?pick=<uuid> restores that saved region once picks + view load.
  const pendingPickRef = useRef<string | null>(params.get("pick"));
  // Stable per-session id so backend can group rapid picks
  const sessionIdRef = useRef<string>(crypto.randomUUID().slice(0, 12));
  const [pendingNote, setPendingNote] = useState<string>("");
  // WM outline overlay on the main canvas. Default ON — reviewers need to
  // see the white-matter boundary they must draw inside of. The minimap is a
  // secondary cue; this draws the boundary on the image itself.
  const [showWm, setShowWm] = useState<boolean>(true);
  // WM overlay display tuning, exposed via a gear popover next to the toggle
  // and remembered across sessions/samples so a reviewer's look sticks.
  const WM_DISPLAY_KEY = "orion_wm_display";
  const wmDisplay0 = (() => {
    try {
      const d = JSON.parse(localStorage.getItem(WM_DISPLAY_KEY) || "null");
      if (d && typeof d === "object") return d;
    } catch {}
    return {};
  })();
  const [wmDim, setWmDim] = useState<number>(
    typeof wmDisplay0.dim === "number" ? wmDisplay0.dim : 0.62);
  const [wmEdgeColor, setWmEdgeColor] = useState<string>(
    wmDisplay0.edgeColor || "#39FF14");
  const [wmEdgeWidth, setWmEdgeWidth] = useState<number>(
    typeof wmDisplay0.edgeWidth === "number" ? wmDisplay0.edgeWidth : 2);
  const [wmGearOpen, setWmGearOpen] = useState<boolean>(false);
  useEffect(() => {
    try {
      localStorage.setItem(WM_DISPLAY_KEY, JSON.stringify(
        {dim: wmDim, edgeColor: wmEdgeColor, edgeWidth: wmEdgeWidth}));
    } catch {}
  }, [wmDim, wmEdgeColor, wmEdgeWidth]);
  // Live overlap of current preview rect against the WM polygon.
  const [liveOverlap, setLiveOverlap] = useState<number | null>(null);
  // Cursor position in container-local screen px (for the locked-rect HUD,
  // which must stay visible regardless of zoom).
  const [cursorScreen, setCursorScreen] = useState<{x:number;y:number} | null>(null);

  // Full sample roster (from manifest.json) powering the in-header
  // disease/sample switcher + prev/next. Every sample exposes every channel in
  // the Viv era, so the roster is marker-independent; the marker only decides
  // which channel is pre-activated.
  const [roster, setRoster] = useState<Array<{sid: string; donor_id: string; condition: string}>>([]);
  // Remember the reviewer's channel on/off + colour choices BY LABEL so they
  // carry across sample switches. Contrast is intentionally NOT preserved — it
  // is re-fit per sample (intensity ranges differ), see the bootstrap effect.
  const channelPrefsRef = useRef<Map<string, {on: boolean; color: string}>>(new Map());

  // --- bootstrap ----------------------------------------------------------
  useEffect(() => {
    (async () => {
      if (!sid) { location.href = "/"; return; }
      // 1. Manifest sample JSON (channels metadata + WM polygon)
      let s: SampleData;
      try { s = await loadSample(sid); }
      catch (e: any) { setError(`sample.json not found: ${e.message}`); return; }
      setSample(s);
      setStatusMsg("Loading OME-Zarr…");

      // 1b. Lab-canonical windows (shared, marker-keyed). Loaded before channel
      //     init so canonical color+lo/hi win over manifest defaults — every
      //     picking session starts on the lab standard.
      const win = await loadWindows();
      setWindows(win);
      setWindowsLoaded(true);

      // 2. Initialize channel state. Colour precedence: canonical palette →
      //    lab-canonical window → reviewer's carried colour → manifest default.
      //    The canonical palette wins so the on-screen picker matches what the
      //    export renders (export_worker.py forces the same palette), keeping
      //    the picker WYSIWYG. on/off carries across a sample switch; contrast is
      //    re-fit per sample for channels that have NO canonical window.
      const prefs = channelPrefsRef.current;
      const cs: ChannelState[] = s.channels.map((c, idx) => {
        const p = prefs.get(c.label);
        const lw = win[c.label];
        const initColor = canonicalColor(c.label) || (lw && lw.color) || p?.color || c.color;
        return {
          idx,
          label: c.label,
          color: initColor,
          def: initColor,
          on: p ? p.on : (!!c.active || isVessel(c.label) || c.label === targetMarker),
          lo: lw ? lw.lo : c.window.start,
          hi: lw ? lw.hi : c.window.end,
        };
      });
      setChannels(cs);
      // Seed auto-contrast as "done" for canonical channels so their fixed
      // lo/hi is never overwritten by the per-sample auto-contrast pass.
      setAutoContrastDone(new Set(cs.filter(c => win[c.label]).map(c => c.idx)));

      // 3. Load OME-Zarr via Viv. Second arg MUST be `{type: "multiscales"}`
      // or viv throws a misleading "Only multiscale OME-Zarr is supported."
      try {
        const ldr = await loadOmeZarr(
            `${(window.PICKER_CONFIG!.DATA_BASE).replace(/\/$/, "")}/${s.zarr_url}`,
            {type: "multiscales"} as any);
        setLoader(ldr);
      } catch (e: any) {
        setError(`OME-Zarr load failed: ${e.message}`);
        return;
      }

      // 4. Initial picks
      try {
        const j = await api(`/picks?sid=${encodeURIComponent(sid)}`);
        setPicks(parsePicks(j.picks || []));
      } catch {}
      setStatusMsg("Ready.");
    })();
  }, [sid, targetMarker]);

  // Load the sample roster once (marker-independent) for the header switcher.
  useEffect(() => {
    (async () => {
      try {
        const m = await (await fetch(dataUrl(manifestUrl()), {cache: "no-store"})).json();
        setRoster((m.samples || []).map((s: any) => ({
          sid: s.sid, donor_id: s.donor_id, condition: s.condition ?? s.cohort})));
      } catch {}
    })();
  }, []);

  // Mirror the reviewer's live channel on/off + colour choices into a ref so a
  // sample switch can re-apply them by label (see bootstrap step 2).
  useEffect(() => {
    const m = channelPrefsRef.current;
    for (const c of channels) m.set(c.label, {on: c.on, color: c.color});
  }, [channels]);

  // Auto-contrast only the channels that are ACTIVE at load. Doing all 16
  // burned the browser's per-host chunk-fetch budget on lowest-pyramid
  // rasters that the user never sees, AND queued those fetches ahead of the
  // user's actual zoom-in requests so L0 tiles couldn't catch up. Other
  // channels auto-contrast lazily the first time the user toggles them on.
  const [autoContrastDone, setAutoContrastDone] = useState<Set<number>>(new Set());
  useEffect(() => {
    if (!loader || !channels.length) return;
    let cancelled = false;
    (async () => {
      const pyramidLevels = loader.data.length;
      const lowest = loader.data[pyramidLevels - 1];
      const targets = channels.filter(c => c.on && !autoContrastDone.has(c.idx));
      if (targets.length === 0) { setStatusMsg("Ready."); return; }
      console.log("[viewer] auto-contrast: pyramid levels =", pyramidLevels,
                  "lowest shape =", lowest.shape,
                  "targets =", targets.map(c => c.label));
      const newCs = channels.slice();
      const done = new Set(autoContrastDone);
      for (const c of targets) {
        try {
          // OME-Zarr axes here are (c, y, x) only — passing z/t in the selection
          // makes viv's indexer throw "Invalid indexer key: z".
          const raster = await lowest.getRaster({selection: {c: c.idx}});
          const stats = getChannelStats(raster.data);
          const limits = stats.contrastLimits || [stats.domain?.[0] ?? 0, stats.domain?.[1] ?? 1000];
          const [lo, hi] = limits;
          const lo2 = Math.round(Math.max(0, lo));
          const hi2 = Math.round(Math.max(lo2 + 1, hi));
          console.log(`[viewer] ${c.label.padEnd(10)} domain=${stats.domain} limits=[${lo}, ${hi}] → [${lo2}, ${hi2}]`);
          const i = newCs.findIndex(x => x.idx === c.idx);
          if (i >= 0) newCs[i] = {...newCs[i], lo: lo2, hi: hi2};
          done.add(c.idx);
        } catch (e: any) {
          console.warn(`[viewer] ${c.label} stats failed:`, e?.message);
        }
      }
      if (!cancelled) {
        setChannels(newCs);
        setAutoContrastDone(done);
        setStatusMsg("Ready.");
      }
    })();
    return () => { cancelled = true; };
    // Re-run when the user toggles a NEW channel on (the targets filter
    // skips already-contrasted ones, so this is cheap on repeats).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loader, channels.map(c => c.on ? c.idx : -1).sort().join(",")]);

  // Poll for export progress while any pick is mid-export. The Minerva poller
  // flips export_status requested→running→done out-of-band, so we re-fetch the
  // pick rows every few seconds until nothing is in flight, then stop.
  const anyExporting = picks.some(
    p => p.export_status === "requested" || p.export_status === "running");
  useEffect(() => {
    if (!sid || !anyExporting) return;
    let stop = false;
    const t = setInterval(async () => {
      try {
        const j = await api(`/picks?sid=${encodeURIComponent(sid)}`);
        if (stop) return;
        const fresh = parsePicks(j.picks || []);
        const byId = new Map(fresh.map(p => [p.uuid, p]));
        setPicks(prev => prev.map(p => {
          const f = byId.get(p.uuid);
          return f ? {...p, export_status: f.export_status,
                      export_url: f.export_url, export_at: f.export_at} : p;
        }));
      } catch {}
    }, 4000);
    return () => { stop = true; clearInterval(t); };
  }, [sid, anyExporting]);

  // --- derived: deck.gl layers + viv selections --------------------------
  const onChannels = channels.filter(c => c.on);

  const initialViewState = useMemo(() => {
    if (!loader) return null;
    const base = loader.data[0];
    const [_, h, w] = base.shape;
    return {
      target: [w / 2, h / 2, 0],
      zoom: -Math.log2(Math.max(w, h) / 1024),
      // L0 tiles now render with LINEAR filtering (see linearRenderSubLayers),
      // so deep zoom stays smooth rather than blocky. Keep a modest cap so
      // magnification doesn't blur into mush. zoom 0 = 1 image px/screen px.
      minZoom: -10, maxZoom: 3,
    };
  }, [loader]);
  useEffect(() => { if (initialViewState) setViewState(initialViewState); }, [initialViewState]);

  // Custom drawing layers (WM polygon + saved picks + active drag)
  const overlayLayers = useMemo(() => {
    if (!sample) return [];
    const ls: any[] = [];
    // NOTE: the WM isolation overlay is NOT a deck.gl layer — those handed to
    // VivViewer via deckProps.layers silently fail to render (that's why the
    // toggle used to do nothing on the main image). It's drawn as a screen-space
    // <WmOverlay> canvas below instead, aligned to the view via viewState.
    if (picks.length) {
      ls.push(new PolygonLayer({
        id: "picks",
        data: picks.map(p => ({
          polygon: [
            [p.x_l0, p.y_l0],
            [p.x_l0 + p.w_l0, p.y_l0],
            [p.x_l0 + p.w_l0, p.y_l0 + p.h_l0],
            [p.x_l0, p.y_l0 + p.h_l0],
          ],
        })),
        getPolygon: (d: any) => d.polygon,
        getFillColor: [239, 68, 68, 30],
        getLineColor: [239, 68, 68, 255],
        lineWidthMinPixels: 2,
        pickable: false,
      }));
    }
    // NOTE: the live drag/selection rectangle is intentionally NOT a deck.gl
    // layer. Custom layers handed to VivViewer via deckProps.layers proved
    // unreliable (they silently failed to render), so the drawn rect is drawn
    // as a plain screen-space <div> in the render tree instead (see dragRect
    // overlay below), matching the locked-rect HUD. Keeping it out of this memo
    // also means mouse-move no longer re-diffs every overlay layer while
    // dragging, which makes the draw interaction noticeably smoother.
    return ls;
  }, [sample, picks, showWm, loader, isLocked]);

  // Track canvas size — DetailView width/height are pixel dimensions, NOT
  // fractions. With width/height = 1 the view renders into a 1×1 px region
  // (invisible). Wire up a ResizeObserver to keep the views sized to the
  // actual canvas-wrap div.
  const [canvasSize, setCanvasSize] = useState<{width: number; height: number}>(
      {width: 800, height: 600});
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const observe = () => {
      const r = el.getBoundingClientRect();
      setCanvasSize({width: Math.max(1, r.width), height: Math.max(1, r.height)});
    };
    observe();
    const ro = new ResizeObserver(observe);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const views = useMemo(() => {
    if (!loader) return [];
    return [new DetailView({id: VIEW_ID,
                              height: canvasSize.height,
                              width:  canvasSize.width})];
  }, [loader, canvasSize.width, canvasSize.height]);

  const onViewportLoad = useCallback((vp: any) => {
    viewportRef.current = vp;
  }, []);

  // Group saved picks by marker so a donor with several markers is legible.
  // Preserves newest-first order within each group; groups ordered by their
  // most-recent pick.
  const pickGroups = useMemo(() => {
    const by = new Map<string, Pick[]>();
    for (const p of picks) {
      const key = (p as any).marker
        || (Array.isArray(p.channels_on) && p.channels_on[0]) || "—";
      (by.get(key) || by.set(key, []).get(key)!).push(p);
    }
    return Array.from(by.entries());
  }, [picks]);

  // Physical size of one L0 pixel; drives µm↔px for presets + HUD labels.
  const pxUm = sample?.px_size_um || 0.325;
  // Effective fixed-square lock: the URL lock_w/lock_h (marker-matched CTL
  // picks) OR the chosen µm preset. When set, the tool is a click-to-place
  // fixed square; otherwise it's a free square drag.
  const presetPx = presetUm > 0 ? Math.round(presetUm / pxUm) : 0;
  const effLockW = isLocked ? lockW : presetPx;
  const effLockH = isLocked ? lockH : presetPx;
  const effLocked = effLockW > 0 && effLockH > 0;
  // Human label for a µm size: keep small values in µm, switch to mm past 1000
  // so big regions don't drown in zeros (1000µm → "1 mm").
  const fmtSize = (um: number) =>
    um >= 1000 ? `${(um / 1000) % 1 === 0 ? um / 1000 : (um / 1000).toFixed(1)} mm`
               : `${Math.round(um)} µm`;

  // --- mouse handling for drag-to-pick -----------------------------------
  function screenToImage(e: React.MouseEvent): [number, number] | null {
    if (!containerRef.current || !viewState) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    // Map container-screen px -> L0 image px directly from the viewState,
    // matching Viv DetailView's OrthographicView (flipY: screen-y-down maps
    // to image-y-down) and the coordinate convention of stored picks. The
    // previous ad-hoc `new OrthographicView(...).makeViewport()` unproject
    // could disagree with the rendered view, placing the drag rect off in
    // empty space so it "never showed".
    //
    // Center on canvasSize (the exact width/height handed to DetailView), NOT
    // the live getBoundingClientRect: deck.gl maps viewState.target to the
    // center of the canvasSize-sized viewport, and the drag-rect overlay draws
    // its inverse with canvasSize/2 too. Using the live rect here instead let a
    // canvasSize↔rect gap leak in as a constant screen offset (the rect drew
    // higher than the cursor). rect.left/top stay live — they're only for the
    // clientX/Y -> container-local conversion, which is exact.
    const zoom = Array.isArray(viewState.zoom) ? viewState.zoom[0] : viewState.zoom;
    const scale = Math.pow(2, zoom);            // screen px per image px
    const [tx, ty] = viewState.target;          // image-px at viewport center
    const x = tx + (cx - canvasSize.width / 2) / scale;
    const y = ty + (cy - canvasSize.height / 2) / scale;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return [x, y];
  }

  function onMouseDown(e: React.MouseEvent) {
    if (mode !== "pick" || e.button !== 0) return;
    const p = screenToImage(e);
    if (!p) return;
    if (effLocked) {
      // Click-to-commit at the fixed square size, centered on the click.
      const r = {x: p[0] - effLockW/2, y: p[1] - effLockH/2, w: effLockW, h: effLockH};
      setDragRect(r);
      // Fire the save on the next tick so React state is updated first.
      setTimeout(() => commitPick(r), 0);
    } else {
      dragStart.current = p;
      setDragRect({x: p[0], y: p[1], w: 0, h: 0});
    }
    e.preventDefault();
  }
  function onMouseMove(e: React.MouseEvent) {
    if (mode !== "pick") return;
    // Track cursor in container-local screen px for the locked HUD overlay.
    if (containerRef.current) {
      const rc = containerRef.current.getBoundingClientRect();
      setCursorScreen({x: e.clientX - rc.left, y: e.clientY - rc.top});
    }
    const p = screenToImage(e);
    if (!p) return;
    if (effLocked) {
      // Show preview rect under cursor at fixed size (no drag needed).
      const r = {x: p[0] - effLockW/2, y: p[1] - effLockH/2, w: effLockW, h: effLockH};
      setDragRect(r);
      if (sample?.wm_polygon?.length) {
        setLiveOverlap(rectInWm(r.x, r.y, r.w, r.h, sample.wm_polygon as Ring[]));
      }
      return;
    }
    if (!dragStart.current) return;
    // Free drag is ALWAYS a perfect square (side = larger of |dx|,|dy|), so an
    // exported region never needs cropping to square afterwards.
    const [sx, sy] = dragStart.current;
    const dx = p[0] - sx, dy = p[1] - sy;
    const s = Math.max(Math.abs(dx), Math.abs(dy));
    const r = {x: dx < 0 ? sx - s : sx, y: dy < 0 ? sy - s : sy, w: s, h: s};
    setDragRect(r);
    if (r.w > 8 && r.h > 8 && sample?.wm_polygon?.length) {
      setLiveOverlap(rectInWm(r.x, r.y, r.w, r.h, sample.wm_polygon as Ring[]));
    }
  }
  // Grab a small JPEG preview of the picked rect straight off the live deck.gl
  // canvas (luma.gl keeps preserveDrawingBuffer on, so a readback is valid).
  // Best-effort: any failure returns null and never blocks the save. The
  // preview lets reviewers eyeball saved regions in "My picks" before they
  // ever queue a full-res Drive export.
  function captureThumb(r: {x:number;y:number;w:number;h:number}): string | null {
    try {
      const wrap = containerRef.current;
      if (!wrap || !viewState) return null;
      // Among the stacked canvases (WmOverlay / Minimap / ScaleBar are 2D),
      // the deck.gl one is the sole WebGL2 context.
      const deckCanvas = Array.from(wrap.querySelectorAll("canvas"))
        .find(c => { try { return !!c.getContext("webgl2"); } catch { return false; } }) as HTMLCanvasElement | undefined;
      if (!deckCanvas) return null;
      const dpr = deckCanvas.width / (deckCanvas.clientWidth || canvasSize.width) || 1;
      const zoom = Array.isArray(viewState.zoom) ? viewState.zoom[0] : viewState.zoom;
      const z = Math.pow(2, zoom);
      const [tx, ty] = viewState.target;
      // Same world→screen mapping as the on-canvas selection overlay, then
      // scaled into the backing buffer's device pixels.
      let sx = Math.round(((r.x - tx) * z + canvasSize.width / 2) * dpr);
      let sy = Math.round(((r.y - ty) * z + canvasSize.height / 2) * dpr);
      let sw = Math.round(r.w * z * dpr);
      let sh = Math.round(r.h * z * dpr);
      sx = Math.max(0, Math.min(sx, deckCanvas.width - 1));
      sy = Math.max(0, Math.min(sy, deckCanvas.height - 1));
      sw = Math.max(1, Math.min(sw, deckCanvas.width - sx));
      sh = Math.max(1, Math.min(sh, deckCanvas.height - sy));
      const maxDim = 280;
      const scale = Math.min(1, maxDim / Math.max(sw, sh));
      const off = document.createElement("canvas");
      off.width = Math.max(1, Math.round(sw * scale));
      off.height = Math.max(1, Math.round(sh * scale));
      const octx = off.getContext("2d");
      if (!octx) return null;
      octx.drawImage(deckCanvas, sx, sy, sw, sh, 0, 0, off.width, off.height);
      return off.toDataURL("image/jpeg", 0.6);
    } catch { return null; }
  }

  // ON channels whose live settings differ from their lab-canonical window.
  // Only markers that HAVE a canonical window are comparable; a marker with no
  // canonical yet isn't "off" anything, so it never gates.
  function adjustedOnChannels() {
    const out: Array<{label: string; cur: {lo:number;hi:number;color:string};
                      canon: MarkerWindow}> = [];
    for (const c of channels) {
      if (!c.on) continue;
      const lw = windows[c.label];
      if (!lw) continue;
      const colDiff = (lw.color || "").toLowerCase() !== (c.color || "").toLowerCase();
      if (c.lo !== lw.lo || c.hi !== lw.hi || colDiff) {
        out.push({label: c.label, cur: {lo: c.lo, hi: c.hi, color: c.color}, canon: lw});
      }
    }
    return out;
  }

  // Save-gate: every saved pick must land on canonical. If any ON channel is
  // off-canonical, prompt (update-canonical | revert-and-preview) instead of
  // saving straight away.
  async function commitPick(r: {x:number;y:number;w:number;h:number}) {
    if (!r || Math.min(r.w, r.h) < 8) { setDragRect(null); setStatusMsg("Rect too small."); return; }
    if (!sample) { setDragRect(null); return; }
    // WM containment is recorded and shown (full/partial/outside) but never
    // blocks a pick — reviewers routinely need context around the mask.
    const overlap = rectInWm(r.x, r.y, r.w, r.h, sample.wm_polygon as Ring[]);
    const adjusted = adjustedOnChannels();
    if (adjusted.length) {
      // Keep the drawn rect on screen behind the modal so revert→preview shows
      // the selected region under canonical settings.
      setPendingPick({r, overlap, adjusted, stage: "choose"});
      return;
    }
    await savePick(r, overlap);
  }

  // Reset the adjusted ON channels back to their canonical lo/hi/color and move
  // the modal to a "preview" stage so the reviewer can eyeball the canonical
  // look before confirming the save.
  function revertToCanonical() {
    setChannels(prev => prev.map(c => {
      const lw = windows[c.label];
      if (c.on && lw) return {...c, lo: lw.lo, hi: lw.hi, color: lw.color || c.color};
      return c;
    }));
    setPendingPick(pp => pp ? {...pp, stage: "reverted"} : pp);
    setStatusMsg("Reverted to lab-canonical — review the image, then Save.");
  }

  // Promote the reviewer's current (off-canonical) settings to the shared lab
  // window for each adjusted marker, then save the pick on those now-canonical
  // values. Exports re-color instantly (windowing is client-side) — no re-crop.
  async function updateCanonicalAndSave() {
    const pp = pendingPick;
    if (!pp) return;
    setStatusMsg("Updating lab-canonical windows…");
    try {
      const rv = getReviewer();
      const by = rv?.name || rv?.email || "";
      const next = {...windows};
      const now = new Date().toISOString();
      for (const a of pp.adjusted) {
        await saveWindow(a.label, {lo: a.cur.lo, hi: a.cur.hi, color: a.cur.color}, by);
        next[a.label] = {lo: a.cur.lo, hi: a.cur.hi, color: a.cur.color,
                         updated_by: by, updated_at: now};
      }
      setWindows(next);
      setPendingPick(null);
      await savePick(pp.r, pp.overlap);
    } catch (err: any) {
      setStatusMsg("Canonical update failed: " + err.message);
    }
  }

  // Promote ONE channel's current color + window to the shared lab-canonical
  // default straight from the channel panel — no pick required. Reuses the same
  // marker_windows store the save-gate writes, so it wins at top precedence for
  // every reviewer on their next load (windowing is client-side → instant, no
  // re-crop). This is the GUI equivalent of editing picker_colors.json.
  async function setChannelDefault(label: string) {
    const c = channels.find(ch => ch.label === label);
    if (!c) return;
    if (!confirm(
        `Set ${label} = ${c.color} as the shared default for EVERY reviewer?\n\n` +
        `This also pins its contrast window (${c.lo}–${c.hi}). It takes effect ` +
        `for everyone on their next load.`)) return;
    const rv = getReviewer();
    const by = rv?.name || rv?.email || "";
    setStatusMsg(`Setting ${label} as the shared default…`);
    try {
      await saveWindow(label, {lo: c.lo, hi: c.hi, color: c.color}, by);
      const now = new Date().toISOString();
      setWindows(prev => ({...prev,
        [label]: {lo: c.lo, hi: c.hi, color: c.color, updated_by: by, updated_at: now}}));
      // Re-baseline so the "changed" affordance clears until the next tweak.
      setChannels(prev => prev.map(ch =>
        ch.label === label ? {...ch, def: c.color} : ch));
      setStatusMsg(`${label} default saved for everyone (${c.color}).`);
    } catch (err: any) {
      setStatusMsg(`Set-default failed: ${err.message}`);
    }
  }

  async function savePick(r: {x:number;y:number;w:number;h:number}, overlap: number) {
    setDragRect(null);
    setStatusMsg("Saving pick…");
    try {
      const onCh = channels.filter(c => c.on);
      const reviewerNow = getReviewer();
      const thumb = captureThumb(r);
      const payload = {
        sid,
        // Carry the marker from the URL (?marker=X) so D1 records the
        // intended target, not whichever channel happens to be first in
        // channels_on. Falls back server-side to channels_on[0] / "*" if
        // the marker page didn't supply one.
        marker: targetMarker || undefined,
        x_l0: Math.round(r.x), y_l0: Math.round(r.y),
        w_l0: Math.round(r.w), h_l0: Math.round(r.h),
        wm_overlap: Math.round(overlap * 1000) / 1000,
        channels_on: onCh.map(c => c.label),
        colormap: Object.fromEntries(onCh.map(c => [c.label, c.color])),
        contrast: Object.fromEntries(onCh.map(c => [c.label, [c.lo, c.hi]])),
        viewport: viewState ? {
          target: Array.isArray(viewState.target) ? viewState.target.slice(0, 2) : null,
          zoom:   typeof viewState.zoom === "number" ? viewState.zoom : null,
        } : null,
        viewer_version: effLocked ? "viv-v1-locked" : "viv-v1",
        reviewer_email: reviewerNow?.email,
        reviewer_name:  reviewerNow?.name,
        session_id: sessionIdRef.current,
        notes: pendingNote.trim()
          || (isLocked ? `locked ${lockW}x${lockH}`
              : presetUm ? `square ${fmtSize(presetUm)} (${effLockW}px)` : null),
        client_uuid: crypto.randomUUID(),
        thumb: thumb || undefined,
      };
      const j = await api("/pick", {method: "POST", body: JSON.stringify(payload)});
      setPicks(prev => [{...j, channels_on: payload.channels_on}, ...prev]);
      setPendingNote("");
      setStatusMsg(`Saved (WM ${wmClass(overlap)}, overlap=${overlap.toFixed(2)})${isLocked ? " [locked]" : presetUm ? ` [${fmtSize(presetUm)}]` : ""}.`);
    } catch (err: any) {
      setStatusMsg("Save FAILED: " + err.message);
    }
  }

  function onMouseUp() {
    if (mode !== "pick" || effLocked) return;
    if (!dragStart.current) return;
    dragStart.current = null;
    const r = dragRect;
    if (!r || Math.min(r.w, r.h) < 8) {
      setDragRect(null);
      setLiveOverlap(null);
      setStatusMsg("Rect too small — drag a larger area.");
      return;
    }
    // Two-step confirm: keep the drawn rect on screen (highlighted) and wait
    // for the reviewer to click "Save Image". commitPick() does the actual
    // POST. This lets them redraw before committing.
    if (sample?.wm_polygon?.length) {
      setLiveOverlap(rectInWm(r.x, r.y, r.w, r.h, sample.wm_polygon as Ring[]));
    }
    setStatusMsg("Rect drawn — click Save Image to keep it.");
  }

  async function deletePick(uuid: string) {
    try {
      await api(`/pick/${uuid}`, {method: "DELETE"});
      setPicks(prev => prev.filter(p => p.uuid !== uuid));
    } catch (err: any) {
      setStatusMsg("Delete failed: " + err.message);
    }
  }

  // Click a saved pick to preview it: restore the viewport (pan+zoom) and the
  // channel visibility / colormap / contrast that were active when it was
  // saved, so the reviewer sees exactly what they picked. Falls back to
  // centering on the rect if no viewport was stored (older picks).
  function restorePick(p: Pick) {
    // 1. Channels: turn on exactly the saved set, applying saved color +
    //    contrast where present; leave everything else off.
    const onSet = new Set(p.channels_on || []);
    if (onSet.size) {
      setChannels(prev => prev.map(c => {
        if (!onSet.has(c.label)) return {...c, on: false};
        const col = p.colormap?.[c.label];
        const con = p.contrast?.[c.label];
        return {
          ...c,
          on: true,
          // Canonical palette wins so a restored pick shows the same colours its
          // export now renders (export forces the canonical palette).
          color: canonicalColor(c.label) || col || c.color,
          lo: con ? con[0] : c.lo,
          hi: con ? con[1] : c.hi,
        };
      }));
    }
    // 2. Viewport: prefer the saved target/zoom; else frame the rect.
    setViewState((vs: any) => {
      const base = vs || initialViewState || {};
      const vp = p.viewport;
      if (vp?.target && typeof vp.zoom === "number") {
        return {...base, target: [vp.target[0], vp.target[1], 0], zoom: vp.zoom};
      }
      const cx = p.x_l0 + p.w_l0 / 2;
      const cy = p.y_l0 + p.h_l0 / 2;
      const z = -Math.log2(Math.max(p.w_l0, p.h_l0) / Math.max(1, canvasSize.width));
      return {...base, target: [cx, cy, 0], zoom: z};
    });
    setStatusMsg(`Previewing pick ${p.uuid.slice(0, 8)}…`);
  }

  // Deep-link restore: once picks + loader + a base view state are ready, if
  // the URL carried ?pick=<uuid>, jump to that saved region exactly once.
  useEffect(() => {
    const want = pendingPickRef.current;
    if (!want || !loader || !viewState || !picks.length) return;
    const p = picks.find(pk => pk.uuid === want);
    if (p) restorePick(p);
    pendingPickRef.current = null;
  }, [picks, loader, viewState]);

  // "Save to Drive": ask the Worker to queue a full-res export for this pick.
  // The Minerva poller does the crop/render/upload and flips export_status to
  // done; we optimistically show "requested" and let polling pick up the rest.
  async function saveToDrive(uuid: string) {
    setStatusMsg("Queuing export…");
    try {
      const updated = await requestExport(uuid);
      setPicks(prev => prev.map(p => p.uuid === uuid
        ? {...p, export_status: updated?.export_status || "requested"}
        : p));
      setStatusMsg("Export queued — it will appear here when ready.");
    } catch (err: any) {
      setStatusMsg("Export request failed: " + err.message);
    }
  }

  // --- sample switching (in-place, no page reload) -----------------------
  // Group by disease for the switcher; keep a stable clinical ordering so the
  // dropdown + prev/next feel predictable regardless of manifest order.
  const CONDITION_ORDER = ["CAD", "CTL", "AD", "PSP"];
  const orderedRoster = useMemo(() => {
    const rank = (c: string) => {
      const i = CONDITION_ORDER.indexOf(c);
      return i < 0 ? CONDITION_ORDER.length : i;
    };
    return [...roster].sort((a, b) =>
      rank(a.condition) - rank(b.condition) ||
      a.donor_id.localeCompare(b.donor_id) ||
      a.sid.localeCompare(b.sid));
  }, [roster]);
  const curIdx = orderedRoster.findIndex(s => s.sid === sid);
  const rosterByCondition = useMemo(() => {
    const m = new Map<string, typeof orderedRoster>();
    for (const s of orderedRoster) {
      if (!m.has(s.condition)) m.set(s.condition, []);
      m.get(s.condition)!.push(s);
    }
    return m;
  }, [orderedRoster]);
  const conditionsPresent = useMemo(() => {
    const seen = new Set(orderedRoster.map(s => s.condition));
    return CONDITION_ORDER.filter(c => seen.has(c))
      .concat([...seen].filter(c => !CONDITION_ORDER.includes(c)).sort());
  }, [orderedRoster]);
  const samplesInCondition = rosterByCondition.get(sample?.condition || "") || [];

  const switchSample = useCallback((nextSid: string) => {
    if (!nextSid || nextSid === sid) return;
    // Reset per-sample transient UI; the bootstrap effect reloads the rest.
    setDragRect(null);
    setLiveOverlap(null);
    setPendingNote("");
    setPicks([]);
    setStatusMsg("Loading sample…");
    setSid(nextSid);
    // Keep the URL shareable/refreshable without a navigation.
    const u = new URL(location.href);
    u.searchParams.set("sid", nextSid);
    history.replaceState({}, "", u.toString());
  }, [sid]);

  const jumpDisease = useCallback((cond: string) => {
    const list = rosterByCondition.get(cond) || [];
    if (list.length) switchSample(list[0].sid);
  }, [rosterByCondition, switchSample]);

  const stepSample = useCallback((dir: -1 | 1) => {
    if (curIdx < 0 || !orderedRoster.length) return;
    const n = (curIdx + dir + orderedRoster.length) % orderedRoster.length;
    switchSample(orderedRoster[n].sid);
  }, [curIdx, orderedRoster, switchSample]);

  // Arrow keys ←/→ step through samples (ignored while typing in a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); stepSample(-1); }
      else if (e.key === "ArrowRight") { e.preventDefault(); stepSample(1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stepSample]);

  // --- render -------------------------------------------------------------
  // Registration + approval gate. Only reviewers whose status is "approved"
  // in the Worker's reviewers table get past this.
  const [rev, setRev] = useState(getReviewer());
  const [revStatus, setRevStatus] = useState<string>(rev?.status || "");
  useEffect(() => {
    // Re-check approval status on every page load — flips quickly once
    // Jonah approves a pending reviewer.
    if (!rev?.email) return;
    fetchStatus(rev.email).then(s => setRevStatus(s));
  }, [rev?.email]);

  if (!rev || (revStatus && revStatus !== "approved")) {
    return <Registration onApproved={() => {
      const r = getReviewer();
      setRev(r);
      setRevStatus("approved");
    }} />;
  }
  if (error) return <div className="error">ERROR: {error}</div>;

  // Calibration gate: block picking on a marker that has no lab-canonical
  // window yet. Lazy + per-marker — you only calibrate the marker you came to
  // pick. Approving writes the shared window (global), so the gate clears for
  // everyone and never reappears for this marker.
  if (targetMarker && windowsLoaded && roster.length && !windows[targetMarker]) {
    return (
      <div className="viewer-layout calib-layout">
        <header>
          <div className="hdr-row hdr-top">
            <div className="brand">
              <span className="brand-mark" aria-hidden>◈</span>
              <span className="brand-name">Orion&nbsp;Picker</span>
              <span className="marker-chip">{targetMarker}</span>
            </div>
            <nav className="hdr-nav"><a href="/">Home</a></nav>
            <div className="hdr-spacer" />
            <div className="who">
              <span className="who-name" title={rev?.email}>{rev?.name}</span>
            </div>
          </div>
        </header>
        <CalibrationGate
          marker={targetMarker}
          roster={roster}
          reviewerName={rev?.name || rev?.email || ""}
          onApproved={(w) => setWindows(prev => ({...prev, [targetMarker]: w}))}
        />
      </div>
    );
  }

  return (
    <div className="viewer-layout">
      <header>
        <div className="hdr-row hdr-top">
          <div className="brand">
            <span className="brand-mark" aria-hidden>◈</span>
            <span className="brand-name">Orion&nbsp;Picker</span>
            {targetMarker && <span className="marker-chip">{targetMarker}</span>}
          </div>
          <nav className="hdr-nav">
            <a href="/">Home</a>
            <a href="/me.html">My picks</a>
            <a href="/exports.html">Exports</a>
          </nav>
          <div className="hdr-spacer" />
          <div className="who">
            <span className="who-name" title={rev?.email}>{rev?.name}</span>
            <button className="btn ghost sm"
                    onClick={() => { clearReviewer(); setRev(null); }}>Sign&nbsp;out</button>
          </div>
        </div>
        <div className="hdr-row hdr-controls">
          <div className="switcher">
            <button className="nav-btn" title="Previous sample (←)"
                    disabled={orderedRoster.length < 2}
                    onClick={() => stepSample(-1)}>‹</button>
            <label className="sel">
              <span className="sel-label">Disease</span>
              <select value={sample?.condition || ""}
                      onChange={e => jumpDisease(e.target.value)}>
                {conditionsPresent.map(c => (
                  <option key={c} value={c}>
                    {c} ({(rosterByCondition.get(c) || []).length})
                  </option>
                ))}
              </select>
            </label>
            <label className="sel sel-wide">
              <span className="sel-label">Sample</span>
              <select value={sid || ""}
                      onChange={e => switchSample(e.target.value)}>
                {samplesInCondition.map(s => (
                  <option key={s.sid} value={s.sid}>{s.donor_id} · {s.sid}</option>
                ))}
              </select>
            </label>
            <button className="nav-btn" title="Next sample (→)"
                    disabled={orderedRoster.length < 2}
                    onClick={() => stepSample(1)}>›</button>
            {curIdx >= 0 && orderedRoster.length > 0 && (
              <span className="pos">{curIdx + 1}<span className="pos-sep">/</span>{orderedRoster.length}</span>
            )}
          </div>
          <div className="divider" />
          <div className="controls">
          <button className={`btn ${mode === "pan" ? "active" : ""}`}
                  onClick={() => setMode("pan")}>Pan/Zoom</button>
          <button className={`btn primary ${mode === "pick" ? "active" : ""}`}
                  onClick={() => setMode("pick")}>
            {effLocked ? `Pick ${fmtSize(effLockW * pxUm)}` : "Select Image"}
          </button>
          {/* Fixed-size square presets so image dimensions are standardized
              across samples/diseases. URL-locked deep-links keep their own
              dims, so the picker is hidden when isLocked. */}
          {!isLocked && (
            <label className="preset-ctl" title="Fixed square size (standardizes image dimensions across samples)">
              <span className="preset-lbl">Size</span>
              <select value={presetUm}
                      onChange={e => {
                        const v = +e.target.value;
                        setPresetUm(v);
                        setDragRect(null);
                        setLiveOverlap(null);
                        if (v > 0) setMode("pick");
                      }}>
                <option value={0}>Free square</option>
                {PRESET_UMS.map(um => (
                  <option key={um} value={um}>{fmtSize(um)}</option>
                ))}
              </select>
            </label>
          )}
          {/* Two-step confirm: once a rect is drawn it stays on screen; the
              reviewer clicks Save Image to commit it (or Redraw to discard). */}
          {!effLocked && dragRect && dragRect.w > 8 && dragRect.h > 8 && (
            <>
              <button className="btn active"
                      onClick={() => commitPick(dragRect)}>
                Save Image
              </button>
              <button className="btn"
                      onClick={() => { setDragRect(null); setLiveOverlap(null); }}>
                Redraw
              </button>
            </>
          )}
          {effLocked && (
            <span className="hud-chip ok">
              🔒 {effLockW}×{effLockH} L0 ({fmtSize(effLockW * pxUm)}) — click to commit
            </span>
          )}
          <div className="wm-ctl">
            <button className={`btn ${showWm ? "active" : ""}`}
                    onClick={() => setShowWm(v => !v)}
                    title="Toggle WM mask overlay (built from OLIG2; permissive/expansive)">
              {showWm ? "WM mask ✓" : "WM mask"}
            </button>
            <button className={`wm-gear ${wmGearOpen ? "open" : ""}`}
                    onClick={() => setWmGearOpen(v => !v)}
                    title="WM mask display settings"
                    aria-label="WM mask display settings">⚙</button>
            {wmGearOpen && (
              <>
                <div className="wm-pop-backdrop" onClick={() => setWmGearOpen(false)} />
                <div className="wm-pop" role="dialog">
                  <div className="wm-pop-hd">WM mask display</div>
                  <label className="wm-pop-row">
                    <span>Dim outside</span>
                    <input type="range" min={0} max={0.9} step={0.02}
                           value={wmDim}
                           onChange={e => setWmDim(parseFloat(e.target.value))} />
                    <b>{Math.round(wmDim * 100)}%</b>
                  </label>
                  <label className="wm-pop-row">
                    <span>Edge color</span>
                    <input type="color" value={wmEdgeColor}
                           onChange={e => setWmEdgeColor(e.target.value)} />
                    <b>{wmEdgeColor.toUpperCase()}</b>
                  </label>
                  <label className="wm-pop-row">
                    <span>Edge width</span>
                    <input type="range" min={0} max={6} step={1}
                           value={wmEdgeWidth}
                           onChange={e => setWmEdgeWidth(parseInt(e.target.value, 10))} />
                    <b>{wmEdgeWidth}px</b>
                  </label>
                  <button className="wm-pop-reset"
                          onClick={() => { setWmDim(0.62); setWmEdgeColor("#39FF14"); setWmEdgeWidth(2); }}>
                    Reset to defaults
                  </button>
                </div>
              </>
            )}
          </div>
          {mode === "pick" && liveOverlap !== null && (() => {
            const cls = wmClass(liveOverlap);
            return (
              <span className={`hud-chip ${cls === "full" ? "ok" : ""}`}>
                WM {cls}: {(liveOverlap * 100).toFixed(0)}%
              </span>
            );
          })()}
          {(() => {
            const off = adjustedOnChannels().length;
            return off > 0 ? (
              <span className="hud-chip warn" title="Off lab-canonical — saving will prompt to update or revert">
                ✎ exploratory: {off} off-canonical
              </span>
            ) : Object.keys(windows).length ? (
              <span className="hud-chip ok" title="All active channels on lab-canonical windows">🔒 canonical</span>
            ) : null;
          })()}
          <span className="status">{statusMsg}</span>
          </div>
        </div>
      </header>
      <main>
        <ChannelPanel channels={channels} onChange={setChannels} canonical={windows}
                      onSetDefault={setChannelDefault} />
        <div className="canvas-wrap" ref={containerRef}
             onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
             style={{cursor: mode === "pick" ? "crosshair" : "grab"}}>
          {loader && viewState && (
            <VivViewer
              views={views}
              layerProps={[{
                loader: loader.data,
                selections: onChannels.map(c => ({c: c.idx})),
                contrastLimits: onChannels.map(c => [c.lo, c.hi]),
                colors: onChannels.map(c => hexToRgb(c.color)),
                channelsVisible: onChannels.map(() => true),
                // Force LINEAR sampling on L0 tiles so deep zoom is smooth,
                // not pixelated (Viv's default uses NEAREST at max res).
                renderSubLayers: linearRenderSubLayers,
                // Progressive pyramid loading: keep the already-loaded coarse
                // tiles on screen while the finer level streams in, instead of
                // blanking to black. Makes panning/zooming feel instant — the
                // low-res pyramid shows immediately, L0 sharpens in as it
                // arrives. (This is TileLayer's default at opacity 1; we set it
                // explicitly so the behavior is guaranteed and documented.)
                refinementStrategy: "best-available",
                // Refine-when-idle: MultiscaleImageLayer forwards these straight
                // to its inner deck.gl TileLayer. debounceTime holds off ALL new
                // tile fetches until the view stops moving ~130 ms, so a
                // pan/zoom fling no longer floods the network with requests for
                // viewports you're just passing through — coarse tiles stay on
                // screen (best-available) and the sharp level loads once you
                // settle. maxRequests lifts the concurrent-fetch cap (default
                // 10); the zarr-proxy is HTTP/2 so many tile streams multiplex
                // on one connection, letting the settle-refine finish faster.
                debounceTime: 130,
                maxRequests: 24,
              }]}
              viewStates={[{...viewState, id: VIEW_ID}]}
              onViewStateChange={({viewState: vs}: any) => setViewState(vs)}
              deckProps={{
                layers: overlayLayers,
                controller: {dragPan: mode === "pan", scrollZoom: true},
              }}
            />
          )}
          {viewState && sample && (
            <ScaleBar
              zoom={Array.isArray(viewState.zoom) ? viewState.zoom[0] : viewState.zoom}
              pxSizeUm={sample.px_size_um || 0.325}
            />
          )}
          {showWm && sample?.wm_polygon?.length && viewState && (
            <WmOverlay
              wmPolygon={sample.wm_polygon as any}
              viewState={viewState}
              canvasW={canvasSize.width}
              canvasH={canvasSize.height}
              dim={wmDim}
              edgeColor={wmEdgeColor}
              edgeWidth={wmEdgeWidth}
            />
          )}
          {sample?.wm_polygon?.length && loader?.data?.[0]?.shape && viewState && (
            <Minimap
              slideW={(loader.data[0].shape as number[])[2]}
              slideH={(loader.data[0].shape as number[])[1]}
              wmPolygon={sample.wm_polygon as any}
              viewState={viewState}
              canvasW={canvasSize.width}
              canvasH={canvasSize.height}
            />
          )}
          {/* Locked-rect HUD — drawn in container-local SCREEN px so it
              stays visible at any zoom, even when the rect itself extends
              far past the viewport edges. Always centered on the cursor,
              or on canvas center if the mouse hasn't entered the viewer
              yet. Visible in BOTH pan and pick mode so the reviewer can
              see the rect dims before clicking; in pan mode it's dimmer
              to signal "not yet armed". */}
          {effLocked && viewState && (() => {
            const cx = cursorScreen?.x ?? canvasSize.width / 2;
            const cy = cursorScreen?.y ?? canvasSize.height / 2;
            const z = Math.pow(2, viewState.zoom);
            const screenW = effLockW * z;
            const screenH = effLockH * z;
            const armed = mode === "pick";
            const stroke = armed ? "#22d3ee" : "#94a3b8";  // cyan armed, slate dimmed
            const fill   = armed ? "rgba(34, 211, 238, 0.10)" : "rgba(148, 163, 184, 0.04)";
            return (
              <>
                {/* Rect outline */}
                <div style={{
                  position: "absolute",
                  left: cx - screenW / 2,
                  top:  cy - screenH / 2,
                  width: screenW, height: screenH,
                  border: `${armed ? 2 : 1.5}px ${armed ? "solid" : "dashed"} ${stroke}`,
                  background: fill,
                  pointerEvents: "none",
                  zIndex: 9,
                }}/>
                {/* Crosshair at cursor / center — always visible */}
                <div style={{
                  position: "absolute",
                  left: cx - 12, top: cy - 1,
                  width: 24, height: 2, background: stroke,
                  pointerEvents: "none", zIndex: 11,
                }}/>
                <div style={{
                  position: "absolute",
                  left: cx - 1, top: cy - 12,
                  width: 2, height: 24, background: stroke,
                  pointerEvents: "none", zIndex: 11,
                }}/>
              </>
            );
          })()}
          {/* Live drag selection — drawn in container-local SCREEN px (same
              approach as the locked HUD above), NOT as a deck.gl layer, so it
              renders reliably regardless of the Viv layer pipeline. Cyan box +
              a 9999px box-shadow that dims everything outside it (spotlight);
              .canvas-wrap has overflow:hidden so the shadow can't bleed out. */}
          {!effLocked && dragRect && viewState && dragRect.w > 0 && dragRect.h > 0 && (() => {
            const zoom = Array.isArray(viewState.zoom) ? viewState.zoom[0] : viewState.zoom;
            const z = Math.pow(2, zoom);
            const [tx, ty] = viewState.target;
            const left = (dragRect.x - tx) * z + canvasSize.width / 2;
            const top  = (dragRect.y - ty) * z + canvasSize.height / 2;
            return (
              <div style={{
                position: "absolute",
                left, top,
                width: dragRect.w * z, height: dragRect.h * z,
                border: "2px solid #22d3ee",
                background: "rgba(34, 211, 238, 0.12)",
                boxShadow: "0 0 0 9999px rgba(3, 5, 8, 0.55)",
                pointerEvents: "none",
                zIndex: 8,
              }}/>
            );
          })()}
        </div>
        <aside className="picks-pane">
          <h2>Saved picks ({picks.length})</h2>
          <div className="note-input">
            <label>Note — attached to your next pick</label>
            <textarea value={pendingNote}
                      placeholder="optional: why this region"
                      onChange={e => setPendingNote(e.target.value.slice(0, 1000))}
                      rows={2} />
          </div>
          {pickGroups.map(([marker, groupPicks]) => (
          <section key={marker} className="pick-group">
            <div className="pick-group-hd">
              <span className="pg-marker">{marker}</span>
              <span className="pg-count">{groupPicks.length}</span>
            </div>
            {groupPicks.map(p => {
            const st = p.export_status || "";
            const busy = st === "requested" || st === "running";
            const done = st === "done";
            const failed = st === "failed";
            return (
            <div key={p.uuid} className="pick-card">
              {/* Click the metadata to restore viewport + channel state. */}
              <div className="meta" style={{cursor: "pointer"}}
                   title="Click to preview: restore this pick's view + channels"
                   onClick={() => restorePick(p)}>
                <span className="dims">
                  {(p.w_l0 * pxUmForSid(p.sid)).toFixed(0)}×{(p.h_l0 * pxUmForSid(p.sid)).toFixed(0)} µm
                </span> · L0 ({p.x_l0}, {p.y_l0})
                {p.wm_overlap != null && (
                  <span className={`wm-pill ${wmClass(p.wm_overlap)}`}
                        title={`WM overlap ${(p.wm_overlap * 100).toFixed(0)}%`}>
                    WM {wmClass(p.wm_overlap)}
                  </span>
                )}<br/>
                <span className="chans">
                  {Array.isArray(p.channels_on) ? p.channels_on.join(" · ") : "(no channels)"}
                </span>
                {(p as any).notes && (
                  <div className="note">“{(p as any).notes}”</div>
                )}
              </div>
              <div className="pick-actions">
                {done && p.export_url ? (
                  <a className="btn open-export" href={p.export_url}
                     target="_blank" rel="noreferrer">
                    ⬇ Open export
                  </a>
                ) : (
                  <button className="btn"
                          disabled={busy}
                          onClick={() => saveToDrive(p.uuid)}
                          style={busy ? {opacity: 0.6} : undefined}>
                    {busy ? (st === "running" ? "Exporting…" : "Queued…")
                          : failed ? "Retry export" : "Save to Drive"}
                  </button>
                )}
                {failed && (
                  <span className="export-pill failed">failed</span>
                )}
                <button className="btn danger"
                        onClick={() => deletePick(p.uuid)}>Delete</button>
              </div>
            </div>
          );})}
          </section>
          ))}
        </aside>
      </main>
      {/* Save-gate: guarantees every saved pick lands on lab-canonical. */}
      {pendingPick && pendingPick.stage === "choose" && (
        <div className="gate-backdrop"
             onClick={() => { setPendingPick(null); setDragRect(null); setStatusMsg("Save cancelled."); }}>
          <div className="gate-modal" onClick={e => e.stopPropagation()}>
            <h3>Off lab-canonical settings</h3>
            <p>
              {pendingPick.adjusted.length} active marker{pendingPick.adjusted.length > 1 ? "s" : ""} differ
              from the shared lab-canonical window. Every saved pick must land on
              canonical settings — choose how to proceed:
            </p>
            <ul className="gate-adj">
              {pendingPick.adjusted.map(a => (
                <li key={a.label}>
                  <span className="ga-mark">{a.label}</span>
                  <span className="ga-cur">yours [{a.cur.lo}–{a.cur.hi}] <i style={{background: a.cur.color}} /></span>
                  <span className="ga-vs">vs</span>
                  <span className="ga-canon">lab [{a.canon.lo}–{a.canon.hi}] <i style={{background: a.canon.color || "#000"}} /></span>
                </li>
              ))}
            </ul>
            <div className="gate-actions">
              <button className="btn"
                      onClick={() => { setPendingPick(null); setDragRect(null); setStatusMsg("Save cancelled."); }}>
                Cancel
              </button>
              <button className="btn" onClick={revertToCanonical}>
                Revert to canonical &amp; preview
              </button>
              <button className="btn primary" onClick={updateCanonicalAndSave}>
                Update lab-canonical &amp; save
              </button>
            </div>
            <p className="gate-note">
              “Update” makes your values the shared window for everyone (all
              exports re-color instantly). “Revert” shows the canonical look
              first so you can confirm before saving.
            </p>
          </div>
        </div>
      )}
      {pendingPick && pendingPick.stage === "reverted" && (
        <div className="gate-bar">
          <span className="gate-bar-msg">Showing lab-canonical settings — save this pick?</span>
          <button className="btn"
                  onClick={() => { setPendingPick(null); setDragRect(null); setStatusMsg("Save cancelled."); }}>
            Cancel
          </button>
          <button className="btn primary"
                  onClick={() => { const pp = pendingPick; setPendingPick(null); savePick(pp.r, pp.overlap); }}>
            Save on canonical
          </button>
        </div>
      )}
    </div>
  );
}
